import type { VoidFunctionComponent } from 'react';
import { useDeleteTeamGroup } from '../../../../../hooks/api';
import { useAddToast } from '../../../../../contexts/ToastContext';
import { useOnConfirm } from '../../../../../contexts/DialogContext';
import { getErrorMessage } from '../../../../../ReactQuery';
import TableRowActions from '../shared/TableRowActions';

type Props = {
  teamGroupId: number;
  teamGroupName: string;
};

/**
 * Component that renders the action buttons for a team group table row
 * Includes edit and delete functionality.
 */
const TeamGroupsTableRowActions: VoidFunctionComponent<Props> = ({
  teamGroupId,
  teamGroupName,
}) => {
  const addToast = useAddToast();
  const onConfirm = useOnConfirm();
  const { mutateAsync: deleteTeamGroup, isLoading: isDeleting } = useDeleteTeamGroup(teamGroupId);

  const handleDeleteTeamGroup = async () => {
    try {
      await deleteTeamGroup();
      addToast({
        type: 'success',
        content: `The team group "${teamGroupName}" has been deleted successfully.`,
      });
    } catch (error: unknown) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, `Failed to delete team group "${teamGroupName}".`),
      });
    }
  };

  const onDeleteTeamGroup = () =>
    onConfirm({
      title: 'Delete Team Group',
      body: `Are you sure you want to delete the team group "${teamGroupName}"?`,
      onConfirm: handleDeleteTeamGroup,
      confirmText: 'Delete',
      confirmBtnType: 'error',
    });

  return (
    <TableRowActions
      teamId={teamGroupId}
      toEdit={`/account/settings/organization/teams/groups/${teamGroupId}`}
      onDelete={onDeleteTeamGroup}
      isDeleting={isDeleting}
    />
  );
};

export default TeamGroupsTableRowActions;
